import React from 'react';
import { flowRight } from 'lodash';
import { ELSButton } from '@els/els-component-button-react';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import AiChatBaseTemplate from '../../components/ai-chat-base-template/AiChatBaseTemplate.component';
import { fetchQuizQuestionsAction } from '../../components/app/app-state-actions.utilities';
import { ELSIcon } from '../../components/els.components';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { QuizQuestionsPanel } from '../../components/quiz-questions-panel/QuizQuestionsPanel.component';
import { QuizPanelMode } from '../../components/quiz-questions-panel/quiz-questions-panel.constants';
import {
  QuizQuestionListItemDto,
  QuizQuestionSelectionDto,
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import { ELSButtonSize, ELSButtonType } from '../../models/button.models';
import { RecordEmpty } from '../../models/common.models';
import { convertMarkdownToPlainText } from '../../utilities/common.utilities';
import { isShowQuizQuestionsNav } from '../ai-chat/ai-chat.utilities';
import { LibraryPageContent, LibraryPageLabels } from '../library-page/LibraryPageContent.component';
import { useLibraryPage } from '../library-page/useLibraryPage.hook';
import { QUIZ_QUESTIONS_PAGE_SIZE } from './quiz-questions.constants';

type QuizQuestionsPanelContent = {
  mode: QuizPanelMode;
  selection: QuizQuestionSelectionDto;
  // How many questions the selection held when the panel opened, for the panel's analytics
  questionCount: number;
};

const QUIZ_QUESTIONS_LABELS: LibraryPageLabels = {
  block: 'c-naa-quiz-questions',
  fieldName: 'quizQuestions',
  title: 'Quiz questions',
  item: 'Quiz question',
  column: 'Question',
  items: 'questions',
  menuId: 'quiz-question-menu-tooltip',
  paginatorId: 'quiz-questions-paginator',
};

const getPromptText = (question: QuizQuestionListItemDto): string => convertMarkdownToPlainText(question.questionData?.qtiData?.prompt);

export type QuizQuestionsProps = ELSPropsFromToastService;

export const QuizQuestionsComponent = (props: QuizQuestionsProps): JSX.Element => {

  const library = useLibraryPage<QuizQuestionListItemDto, QuizQuestionsPanelContent>({
    fetchList: fetchQuizQuestionsAction,
    pageSize: QUIZ_QUESTIONS_PAGE_SIZE,
    isShowNav: isShowQuizQuestionsNav,
    getRowText: getPromptText,
    toastService: props.toastService,
  });
  const openPanel = (mode: QuizPanelMode) => {
    library.openPanel({
      mode,
      selection: library.selection,
      questionCount: library.state.response?.totalItems,
    });
  };

  const canViewOrTake = library.itemCount > 0;
  const { panelContent } = library;

  return (
    <AiChatBaseTemplate
      isPanelOpen={!!panelContent}
      panelContent={panelContent && (
        <div className="c-naa-panel">
          <QuizQuestionsPanel
            key={library.panelKey}
            mode={panelContent.mode}
            selection={panelContent.selection}
            questionCount={panelContent.questionCount}
            onComplete={library.closePanel}
            onError={library.handlePanelError}
            headerEnd={(
              <button type="button" className="u-els-anchorize" onClick={library.closePanel}>
                <ELSIcon name="close" size="1x" />
                <span className="u-els-hide-visually">Close quiz questions</span>
              </button>
            )}
          />
        </div>
      )}
    >
      <LibraryPageContent
        library={library}
        labels={QUIZ_QUESTIONS_LABELS}
        actions={(
          <>
            <FlexItem>
              <ELSButton
                preventAutoPending
                type={ELSButtonType.PRIMARY}
                size={ELSButtonSize.SMALL}
                isDisabled={!canViewOrTake}
                onClick={() => openPanel(QuizPanelMode.VIEW)}
              >
                {`View questions (${library.itemCount})`}
              </ELSButton>
            </FlexItem>
            <FlexItem>
              <ELSButton
                preventAutoPending
                type={ELSButtonType.PRIMARY}
                size={ELSButtonSize.SMALL}
                isDisabled={!canViewOrTake}
                onClick={() => openPanel(QuizPanelMode.TAKE)}
              >
                {`Take quiz (${library.itemCount})`}
              </ELSButton>
            </FlexItem>
          </>
        )}
      />
    </AiChatBaseTemplate>
  );
};

const enhance = flowRight(
  ELSWithToastService,
  withHTMLHeadSEO({ title: 'Quiz questions' }),
);

const QuizQuestions: React.FC<RecordEmpty> = enhance(QuizQuestionsComponent);

export default QuizQuestions;
